import React, { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { formatDateTimeForDisplay } from '../utils/timezone';

const ImageGallery = ({ images, onImageDeleted, onImageUpdated }) => {
  const api = useApi();
  const [selectedImage, setSelectedImage] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Get the correct API base URL
  const getApiBaseUrl = () => {
    // Use environment variable for production, fallback to origin for development
    if (process.env.REACT_APP_API_BASE_URL) {
      return process.env.REACT_APP_API_BASE_URL;
    }
    
    // Fallback for development
    const isDevelopment = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    return isDevelopment ? 'http://localhost:3050' : window.location.origin;
  };

  const handleDownload = async (image) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/images/${image.id}/file`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = image.originalName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    const errorDiv = e.target.nextElementSibling;
    if (errorDiv) {
      errorDiv.style.display = 'flex';
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await api({
        method: 'delete',
        url: `/images/${imageId}`
      });

      if (response.data.success) {
        onImageDeleted(imageId);
        if (selectedImage && selectedImage.id === imageId) {
          setSelectedImage(null);
        }
      } else {
        setError(response.data.message || 'Error deleting image');
      }
    } catch (err) {
      console.error('Error deleting image:', err);
      setError(err.response?.data?.message || 'Error deleting image');
    } finally {
      setLoading(false);
    }
  };

  const handleEditDescription = async (imageId) => {
    setLoading(true);
    setError('');

    try {
      const response = await api({
        method: 'patch',
        url: `/images/${imageId}`,
        data: { description: editDescription }
      });

      if (response.data.success) {
        onImageUpdated(response.data.data.image);
        setEditingImage(null);
        setEditDescription('');
      } else {
        setError(response.data.message || 'Error updating image description');
      }
    } catch (err) {
      console.error('Error updating image description:', err);
      setError(err.response?.data?.message || 'Error updating image description');
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTimeForDisplay(dateString);
  };

  if (images.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="bi bi-images text-muted" style={{ fontSize: '3rem' }}></i>
        <p className="text-muted mt-2">No images uploaded yet</p>
        <p className="text-muted small">Upload images to see them displayed here</p>
      </div>
    );
  }

  return (
    <>
      {/* Image Grid */}
      <div className="row g-3">
        {images.map((image) => (
          <div key={image.id} className="col-6 col-sm-4 col-md-3 col-lg-2">
            <div className="card h-100 shadow-sm border-0">
              {/* Image Container */}
              <div className="position-relative" style={{ aspectRatio: '1' }}>
                <img
                  src={`${getApiBaseUrl()}/api/images/${image.id}/file`}
                  alt={image.originalName}
                  className="card-img-top h-100 w-100"
                  style={{ 
                    objectFit: 'contain',
                    backgroundColor: '#f8f9fa',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleImageClick(image)}
                  onError={handleImageError}
                />
                
                {/* Error fallback */}
                <div 
                  className="position-absolute top-0 start-0 w-100 h-100 d-none align-items-center justify-content-center"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
                  <div className="text-center">
                    <i className="bi bi-image text-muted" style={{ fontSize: '2rem' }}></i>
                    <p className="text-muted small mt-2">Image not available</p>
                  </div>
                </div>

                {/* Action buttons overlay */}
                <div className="position-absolute top-0 end-0 p-2">
                  <div className="d-flex flex-column gap-1">
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteImage(image.id);
                      }}
                      disabled={loading}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #dc3545',
                        color: '#dc3545'
                      }}
                    >
                      <i className="bi bi-trash" style={{ fontSize: '0.75rem' }}></i>
                    </button>
                    <a
                      href={`${getApiBaseUrl()}/api/images/${image.id}/file`}
                      download={image.originalName}
                      className="btn btn-sm btn-outline-success"
                      onClick={e => e.stopPropagation()}
                      // Remove this line: target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid #198754',
                        color: '#198754'
                      }}
                    >
                      <i className="bi bi-download" style={{ fontSize: '0.75rem' }}></i>
                    </a>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="card-body p-3">
                <div className="mb-2">
                  <small className="text-muted d-block text-truncate" title={image.originalName}>
                    <strong>{image.originalName}</strong>
                  </small>
                  <small className="text-muted d-block">
                    {formatFileSize(image.fileSize)}
                  </small>
                  {image.description && (
                    <p className="mt-1 mb-0 small text-muted text-truncate" title={image.description}>
                      {image.description}
                    </p>
                  )}
                  <small className="text-muted d-block mt-1">
                    {formatDate(image.createdAt)}
                  </small>
                </div>
                <button
                  className="btn btn-sm btn-outline-primary w-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditingImage(image);
                    setEditDescription(image.description || '');
                  }}
                  style={{
                    fontSize: '0.75rem',
                    padding: '0.375rem 0.75rem'
                  }}
                >
                  <i className="bi bi-pencil me-1"></i>
                  Edit
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Image Preview Modal */}
      {selectedImage && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={{ zIndex: 1070 }}
            onClick={() => setSelectedImage(null)}
          ></div>
          <div className="modal fade show d-block" style={{ zIndex: 1071 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h5 className="modal-title text-truncate">
                    <i className="bi bi-image me-2"></i>
                    {selectedImage.originalName}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedImage(null)}
                  ></button>
                </div>
                <div className="modal-body text-center p-0">
                  <div className="position-relative bg-light rounded m-3" style={{ 
                    minHeight: '60vh',
                    maxHeight: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={`${getApiBaseUrl()}/api/images/${selectedImage.id}/file`}
                      alt={selectedImage.originalName}
                      className="img-fluid"
                      style={{ 
                        maxHeight: '100%', 
                        maxWidth: '100%',
                        objectFit: 'contain',
                        padding: '1rem'
                      }}
                      onError={handleImageError}
                    />
                  </div>
                  <div className="px-3 pb-3">
                    {selectedImage.description && (
                      <div className="mt-3">
                        <strong>Description:</strong>
                        <p className="mb-0">{selectedImage.description}</p>
                      </div>
                    )}
                    <div className="text-muted mt-2">
                      <small>
                        <i className="bi bi-file-earmark me-1"></i>
                        Size: {formatFileSize(selectedImage.fileSize)} | 
                        <i className="bi bi-calendar me-1 ms-2"></i>
                        Uploaded: {formatDate(selectedImage.createdAt)}
                      </small>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedImage(null)}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Close
                  </button>
                  <a
                    href={`${getApiBaseUrl()}/api/images/${selectedImage.id}/file`}
                    download={selectedImage.originalName}
                    className="btn btn-outline-success"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="bi bi-download me-1"></i>
                    Download Image
                  </a>
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={() => {
                      handleDeleteImage(selectedImage.id);
                      setSelectedImage(null);
                    }}
                    disabled={loading}
                  >
                    <i className="bi bi-trash me-2"></i>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Edit Description Modal */}
      {editingImage && (
        <>
          <div 
            className="modal-backdrop fade show" 
            style={{ zIndex: 1070 }}
            onClick={() => {
              setEditingImage(null);
              setEditDescription('');
            }}
          ></div>
          <div className="modal fade show d-block" style={{ zIndex: 1071 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-pencil me-2"></i>
                    Edit Image Description
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setEditingImage(null);
                      setEditDescription('');
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger" role="alert">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label">
                      <i className="bi bi-card-text me-1"></i>
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      rows="3"
                      placeholder="Enter image description..."
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditingImage(null);
                      setEditDescription('');
                    }}
                    disabled={loading}
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleEditDescription(editingImage.id)}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-1"></i>
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ImageGallery; 